import { isTaskOpen, isRootTask, getTaskCost, getNotifiedUsers as getTaskNotifiedUsers } from './task.js';

export const CategoryId = {
  ALL: 0,
  ALARM: '1.0',
  ALARM_INTRUSION: '1.1',
  ALARM_FIRE: '1.2',
  ALARM_MEDICAL: '1.3',
  ALARM_PANIC: '1.4',
  STATUS: '2.0',
  STATUS_ARM: '2.1',
  STATUS_DISARM: '2.2',
  TROUBLE: '4.0',
  END_OF_TROUBLE: '5.0',
  TEST: '6.0',
  WARNING: '7.0',
  OTHER: '8.0',
  SERVICE: '9.0',
  EDIT: '10.0',
  PHYSICAL_SECURITY: '11.0',
};
/**
 * Checks if an event is open by verifying all related tasks
 * An event is open if at least one of the related tasks is not closed
 * @param {Object} event - event object with list of tasks
 * @returns {boolean} - true if event is open, false if closed
 */
export function isEventOpen(event) {
  // Check if event has tasks property
  if (!event || !event.tasks || !Array.isArray(event.tasks)) {
    return false;
  }

  // If there are no tasks, event is closed
  if (event.tasks.length === 0) {
    return false;
  }

  // Check if any task is still open using existing task utility
  return event.tasks.some((task) => isTaskOpen(task));
}

/**
 * Checks if an event is closed by verifying all related tasks
 * An event is closed if all related tasks are closed
 * @param {Object} event - event object with list of tasks
 * @returns {boolean} - true if event is closed, false if open
 */
export function isEventClosed(event) {
  return !isEventOpen(event);
}

/**
 * Updates a task in the event's tasks array
 * @param {Object} event - event object containing tasks array
 * @param {Object} task - task object to update
 */
export function updateTask(event, task) {
  if (!event || !event.tasks || !Array.isArray(event.tasks)) {
    return;
  }

  const index = event.tasks.findIndex((item) => item.id == task.id);
  if (index >= 0) {
    event.tasks[index] = task;
  }
}

export function getRootTask(event) {
  if (!event || !event.tasks || !Array.isArray(event.tasks)) {
    return null;
  }
  return event.tasks.find((task) => {
    return isRootTask(task);
  });
}

/**
 * Gets costs for an event by filtering tasks with costs and mapping them
 * @param {Object} event - event object containing tasks array
 * @param {Object} store - Vuex store instance for payments lookup
 * @returns {Array} - array of cost objects with name, icon, color, textColor
 */
export function getEventCosts(event, store) {
  if (!event || !event.tasks || !Array.isArray(event.tasks)) {
    return [];
  }

  // Performance optimization: use reduce instead of map + filter
  return event.tasks.reduce((costs, task) => {
    const cost = getTaskCost(task, store);
    if (cost !== null) {
      costs.push(cost);
    }
    return costs;
  }, []);
}

/**
 * Returns a deduplicated array of all notified users from event's tasks.
 * Assumes each task has a 'notified_users' array of user objects or IDs.
 * @param {Object} event - event object containing tasks array
 * @returns {Array} - array of unique notified users
 */
export function getNotifiedUsers(event) {
  if (!event || !event.tasks || !Array.isArray(event.tasks)) {
    return [];
  }

  // Efficiently collect and deduplicate notified users
  const seen = new Set();
  const result = [];
  for (const task of event.tasks) {
    const notified = getTaskNotifiedUsers(task);
    if (Array.isArray(notified)) {
      for (const user of notified) {
        const id = typeof user === 'object' ? user.id : user;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(user);
        }
      }
    }
  }
  return result;
}

export class Events {}

export class Categories {
  static get(id) {
    return this.all().find((item) => item.id == id);
  }

  static all() {
    return [
      { id: CategoryId.ALL, name: 'Wszystkie', color: 'teal', onColor: 'white', icon: 'mdi-view-list' },
      {
        id: CategoryId.ALARM,
        name: 'Alarm',
        color: 'red',
        onColor: 'white',
        icon: 'mdi-bell-outline',
      },
      {
        id: CategoryId.ALARM_INTRUSION,
        name: 'Włamaniowy',
        color: 'red',
        onColor: 'white',
        icon: 'mdi-shield-home-outline',
      },
      {
        id: CategoryId.ALARM_FIRE,
        name: 'Pożarowy',
        color: 'red',
        onColor: 'white',
        icon: 'mdi-fire',
      },
      {
        id: CategoryId.ALARM_MEDICAL,
        name: 'Medyczny',
        color: 'red',
        onColor: 'white',
        icon: 'mdi-hospital-box-outline',
      },
      {
        id: CategoryId.ALARM_PANIC,
        name: 'Przymusowy',
        color: 'red',
        onColor: 'white',
        icon: 'mdi-alert-octagon-outline',
      },
      {
        id: CategoryId.TROUBLE,
        name: 'Awaria',
        color: 'orange',
        onColor: 'white',
        icon: 'mdi-alert-outline',
      },
      {
        id: CategoryId.END_OF_TROUBLE,
        name: 'Koniec awarii',
        color: 'green',
        onColor: 'white',
        icon: 'mdi-check',
      },
      {
        id: CategoryId.TEST,
        name: 'Test',
        color: 'indigo',
        onColor: 'white',
        icon: 'mdi-wrench-outline',
      },
      {
        id: CategoryId.WARNING,
        name: 'Uwaga',
        color: 'pink',
        onColor: 'white',
        icon: 'mdi-exclamation-thick',
      },
      {
        id: CategoryId.STATUS,
        name: 'Status',
        color: 'blue',
        onColor: 'white',
        icon: 'mdi-check',
      },
      {
        id: CategoryId.STATUS_ARM,
        name: 'Uzbrojenia',
        color: 'blue',
        onColor: 'white',
        icon: 'mdi-shield-lock-outline',
      },
      {
        id: CategoryId.STATUS_DISARM,
        name: 'Rozbrojenia',
        color: 'blue',
        onColor: 'white',
        icon: 'mdi-shield-off-outline',
      },
      {
        id: CategoryId.SERVICE,
        name: 'Serwis',
        color: 'green',
        onColor: 'white',
        icon: 'mdi-wrench-outline',
      },
      {
        id: CategoryId.EDIT,
        name: 'Edycja bazy',
        color: 'purple',
        onColor: 'white',
        icon: 'pencil-outline',
      },
      {
        id: CategoryId.PHYSICAL_SECURITY,
        name: 'Ochrona fizyczna',
        color: 'brown',
        onColor: 'white',
        icon: 'mdi-shield-account-outline',
      },
      {
        id: CategoryId.OTHER,
        name: 'Inne',
        color: 'grey',
        onColor: 'white',
        icon: 'mdi-help',
      },
    ];
  }
}

export default new Events();
